import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { socket } from "../../socket";
import DashboardCustomers from "./DashboardCustomers";
import DashboardWorkOrders from "./DashboardWorkOrders";
import DashboardQuotes from "./DashboardQuotes";
import API from "services/axios";
import authHeader from "services/auth-header";
import Modal from "react-bootstrap/Modal";
import LocationsAdd from "../settings/locations/locationsAdd";
import ShipmentMethodsAdd from "../settings/shipmentMethods/shipmentMethodsAdd";
import PaymentTermsAdd from "../settings/paymentTerms/paymentsTermsAdd";
import CouriersAdd from "../settings/Couriers/CouriersAdd";
import SalesPersonsAdd from "../settings/salesPersons/salesPersonsAdd";

export default function Dashboard() {
  /*   All States
   ********************************************* */
  const user = useSelector((state) => state?.user?.userInfo);

  const [isConnected, setIsConnected] = useState(socket.connected);
  const [missingSetup, setMissingSetup] = useState([]);
  const [setupError, setSetupError] = useState(null);
  const [locationsData, setLocationsData] = useState([]);
  const [shipmentMethodsData, setShipmentMethodsData] = useState([]);
  const [paymentTermsData, setPaymentTermsData] = useState([]);
  const [couriersData, setCouriersData] = useState([]);
  const [salesPersonsData, setSalesPersonsData] = useState([]);

  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCourierModal, setShowCourierModal] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [showProductTypeModal, setShowProductTypeModal] = useState(false);
  const [productTypeForm, setProductTypeForm] = useState({
    productTypes: "",
    showOnJobBoard: true,
  });
  const [productTypeError, setProductTypeError] = useState(null);
  const [productTypeSaving, setProductTypeSaving] = useState(false);
  const navigate = useNavigate();

  /*   All Functions
   ********************************************* */

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("updateQuotation", onDisconnect);
    };
  }, [socket]);

  useEffect(() => {
    let user = JSON.parse(localStorage.getItem("user"));
    if (user?.roles === "customer") {
      navigate("/work-orders");
    }
  }, []);

  const fetchSetupState = useCallback(async () => {
    try {
      const headers = authHeader();
      const [
        locationsRes,
        shipmentMethodsRes,
        paymentTermsRes,
        couriersRes,
        salesPersonsRes,
        productTypesRes,
      ] = await Promise.all([
        API.get("/locations", { headers }),
        API.get("/shipmentmethods", { headers }),
        API.get("/paymentTerms", { headers }),
        API.get("/courier-services", { headers }),
        API.get("/salespersons", { headers }),
        API.post(
          "/sales/products/product-types",
          { page: 1, limit: 1, filterBy: "all" },
          { headers },
        ),
      ]);

      const locations = locationsRes?.data || [];
      const shipmentMethods = shipmentMethodsRes?.data || [];
      const paymentTerms = paymentTermsRes?.data || [];
      const couriers = couriersRes?.data || [];
      const salesPersons = salesPersonsRes?.data || [];
      const productTypes = productTypesRes?.data?.data || [];

      setLocationsData(locations);
      setShipmentMethodsData(shipmentMethods);
      setPaymentTermsData(paymentTerms);
      setCouriersData(couriers);
      setSalesPersonsData(salesPersons);

      const missing = [];

      const hasLocationWithPdfLogo = locations.some((loc) => !!loc?.pdfLogo);
      if (!locations.length || !hasLocationWithPdfLogo) {
        missing.push({
          key: "locations",
          message: "Add at least one brand with a PDF logo",
        });
      }

      if (!shipmentMethods.length) {
        missing.push({
          key: "shipments",
          message: "Add at least one shipment method",
        });
      }

      if (!paymentTerms.length) {
        missing.push({
          key: "paymentTerms",
          message: "Add at least one payment term",
        });
      }

      if (!couriers.length) {
        missing.push({ key: "couriers", message: "Add at least one courier" });
      }

      if (!salesPersons.length) {
        missing.push({
          key: "salesPersons",
          message: "Add at least one sales person",
        });
      }

      if (!productTypes.length) {
        missing.push({
          key: "productTypes",
          message: "Add at least one product type",
        });
      }

      setMissingSetup(missing);
      setSetupError(null);
    } catch (err) {
      setSetupError(err?.message || "Unable to verify setup");
    }
  }, [user]);

  useEffect(() => {
    if (user?.roles !== "customer") {
      fetchSetupState();
    }
  }, [user, fetchSetupState]);

  const lastLocationSortId =
    locationsData?.[locationsData.length - 1]?.sortId || 0;
  const lastShipmentSortId =
    shipmentMethodsData?.[shipmentMethodsData.length - 1]?.sortId || 0;
  const lastPaymentSortId =
    paymentTermsData?.[paymentTermsData.length - 1]?.sortId || 0;
  const lastSalesSortId =
    salesPersonsData?.[salesPersonsData.length - 1]?.sort_id || 0;

  return (
    <div className="pt-5" style={{ marginLeft: 280, height: "90vh" }}>
      {(setupError || missingSetup.length > 0) && (
        <div className="container px-4">
          <div className="alert alert-info" role="alert">
            <div className="d-flex flex-column gap-2">
              <span>
                {setupError
                  ? `Settings check failed: ${setupError}`
                  : "Please complete company settings for normal app operation."}
              </span>
              {missingSetup.length > 0 && (
                <ul className="mb-0 d-flex flex-column gap-1">
                  {missingSetup.map((item) => (
                    <li
                      key={item.key}
                      className="d-flex align-items-center justify-content-between gap-2"
                    >
                      <span>{item.message}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          if (item.key === "locations")
                            setShowLocationModal(true);
                          if (item.key === "shipments")
                            setShowShipmentModal(true);
                          if (item.key === "paymentTerms")
                            setShowPaymentModal(true);
                          if (item.key === "couriers")
                            setShowCourierModal(true);
                          if (item.key === "salesPersons")
                            setShowSalesModal(true);
                          if (item.key === "productTypes")
                            setShowProductTypeModal(true);
                        }}
                      >
                        Add now
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!setupError && (
                <span className="fw-semibold">
                  Go to Settings to add the missing data.
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="container text-center mx-auto">
        {/* <h3>{isConnected ? "Socket connected" : "Socket not connected"}</h3> */}
      </div>
      <div className="d-flex justify-content-center align-items-stretch gap-3 flex-wrap ">
        {user?.roles === "admin" || user?.permissions?.includes("dashboard") ? (
          <>
            <DashboardWorkOrders />
            <DashboardQuotes />
            <DashboardCustomers />
          </>
        ) : (
          <div className=""></div>
        )}
      </div>

      <Modal
        show={showLocationModal}
        onHide={() => setShowLocationModal(false)}
        size="lg"
      >
        <Modal.Body className="custom-shadow">
          <LocationsAdd
            showAddPage={setShowLocationModal}
            refreshFunc={fetchSetupState}
            lastSortId={lastLocationSortId}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={showShipmentModal}
        onHide={() => setShowShipmentModal(false)}
      >
        <Modal.Body className="custom-shadow">
          <ShipmentMethodsAdd
            showAddPage={setShowShipmentModal}
            refreshFunc={fetchSetupState}
            lastSortId={lastShipmentSortId}
          />
        </Modal.Body>
      </Modal>

      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)}>
        <Modal.Body className="custom-shadow">
          <PaymentTermsAdd
            showAddPage={setShowPaymentModal}
            refreshFunc={fetchSetupState}
            lastSortId={lastPaymentSortId}
          />
        </Modal.Body>
      </Modal>

      <Modal show={showCourierModal} onHide={() => setShowCourierModal(false)}>
        <Modal.Body className="custom-shadow">
          <CouriersAdd
            showAddPage={setShowCourierModal}
            refreshFunc={fetchSetupState}
            lastSortId={couriersData?.length || 0}
          />
        </Modal.Body>
      </Modal>

      <Modal show={showSalesModal} onHide={() => setShowSalesModal(false)}>
        <Modal.Body className="custom-shadow">
          <SalesPersonsAdd
            showAddPage={setShowSalesModal}
            refreshFunc={fetchSetupState}
            lastSortId={lastSalesSortId}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={showProductTypeModal}
        onHide={() => setShowProductTypeModal(false)}
      >
        <Modal.Body className="custom-shadow">
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Add Product Type</h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setShowProductTypeModal(false)}
            ></button>
          </div>
          {productTypeError && (
            <div className="alert alert-danger py-2 mb-3">
              {productTypeError}
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">Product Type</label>
            <input
              className="form-control"
              value={productTypeForm.productTypes}
              onChange={(e) =>
                setProductTypeForm((prev) => ({
                  ...prev,
                  productTypes: e.target.value,
                }))
              }
              placeholder="Enter product type"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Show on job board</label>
            <select
              className="form-select"
              value={productTypeForm.showOnJobBoard ? "true" : "false"}
              onChange={(e) =>
                setProductTypeForm((prev) => ({
                  ...prev,
                  showOnJobBoard: e.target.value === "true",
                }))
              }
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowProductTypeModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={productTypeSaving}
              onClick={async () => {
                if (!productTypeForm.productTypes?.trim()) {
                  setProductTypeError("Product type is required");
                  return;
                }
                setProductTypeError(null);
                setProductTypeSaving(true);
                try {
                  const headers = authHeader();
                  const { data } = await API.post(
                    "/sales/products/product-type",
                    productTypeForm,
                    { headers },
                  );
                  if (data?.success) {
                    setShowProductTypeModal(false);
                    setProductTypeForm({
                      productTypes: "",
                      showOnJobBoard: true,
                    });
                    await fetchSetupState();
                  } else {
                    setProductTypeError(
                      data?.message || "Unable to add product type",
                    );
                  }
                } catch (err) {
                  setProductTypeError(
                    err?.message || "Unable to add product type",
                  );
                } finally {
                  setProductTypeSaving(false);
                }
              }}
            >
              Add
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
